import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { CreateOfficeInput, OfficeType, useCreateOfficeMutation, useUpdateOfficeMutation } from '../../../generated'
import { CANCEL, US_STATES } from '../../constants'
import { OfficeModalProps } from '../../interfaceTypes/props'
import { createOfficeSchema } from '../../validationSchema'
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
import CustomPhoneController from '../common/CustomPhoneController'
import CheckboxController from '../common/CheckboxController'
import RadioController from '../common/RadioController'

const OfficeModal = ({ office, isOpen, handleClose, refetch }: OfficeModalProps) => {

  const methods = useForm<CreateOfficeInput>({
    resolver: yupResolver(createOfficeSchema),
    defaultValues: office
      ? {
          ...office,
          addresses: office.addresses?.map(addr => addr || undefined) || []
        }
      : {
          isLocationOpen: true
        }
  })

  const { handleSubmit, reset, control } = methods

  const [createOffice, { loading }] = useCreateOfficeMutation({
    onError(error) {
      if (error.message === 'Not Found Exception') Alert.error('Invalid Phone')
      else Alert.error(error.message)
    },
    onCompleted(data) {
      Alert.success('Profit Center Created')
      console.log('created  Profit Center', data)
      handleClose()
      refetch?.()
      reset()
    }
  })

  const [updateOffice, { loading: updating }] = useUpdateOfficeMutation({
    onError(error) {
      if (error.message === 'Not Found Exception') Alert.error('Invalid Phone')
      else Alert.error(error.message)
    },
    onCompleted(data) {
      Alert.success('Profit Center Updated')
      console.log('updated Profit Center', data)
      handleClose()
      refetch?.()
      reset()
    }
  })

  const onSubmit = async (data: CreateOfficeInput) => {
    console.log('data', data)

    const { name, addresses, description, email, phone, isLocationOpen, officeNumber, type } = data
    const addressInput = addresses
      ?.map(addr => ({
        state: addr.state,
        city: addr.city,
        zipCode: addr.zipCode,
        country: addr.country,
        address1: addr.address1,
        address2: addr.address2
      }))
      ?.filter(addr => addr.city || addr.state || addr.zipCode || addr.country || addr.address1)
    const input = {
      name,
      addresses: addressInput,
      description,
      email,
      phone,
      isLocationOpen,
      officeNumber,
      type
    }
    office?.id
      ? await updateOffice({
          variables: {
            updateOfficeInput: { ...input, id: office.id, addressId: office?.addresses?.[0]?.id }
          }
        })
      : await createOffice({
          variables: {
            createOfficeInput: input
          }
        })
  }

  return (
    <CustomModal title="Profit Center Details" isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} rowSpacing={2}>
            <Grid item md={12} sm={12} xs={12}>
              <RadioController
                row
                name="type"
                defaultValue={OfficeType.ProfitCenter}
                options={[
                  { label: 'Profit Center', value: OfficeType.ProfitCenter },
                  { label: 'Location', value: OfficeType.Location }
                ]}
              />
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController controllerLabel="Office ID" controllerName="officeNumber" />
            </Grid>
            <Grid item md={3} sm={12} xs={12}>
              <CustomController controllerLabel="Name" controllerName="name" />
            </Grid>
            <Grid item md={3} sm={12} xs={12}>
              <CustomPhoneController controllerLabel="Phone" controllerName="phone" />
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerLabel="Email" controllerName="email" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address1"
                controllerLabel="Address line 1"
                fieldType="text"
              />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address2"
                controllerLabel="Address line 2"
                fieldType="text"
              />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerName="addresses[0].city" controllerLabel="City" fieldType="text" />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                <InputLabel id="address-state">State</InputLabel>

                <Controller
                  name="addresses.0.state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="address-state"
                      label="State"
                      fullWidth
                      MenuProps={{ style: { maxHeight: 300 } }}
                    >
                      {US_STATES.map((item, index) => {
                        const { name } = item || {}
                        return (
                          <MenuItem value={name} key={`${item}-${index}`}>
                            {name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController controllerName="addresses.0.zipCode" controllerLabel="Zip Code" fieldType="text" />
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].country"
                controllerLabel="Country"
                fieldType="text"
                defaultValue="USA"
                readOnly
              />
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <Box display={'flex'} alignItems={'center'} gap={3}>
                <CustomController
                  controllerLabel="Description"
                  controllerName="description"
                  isMultiLine
                  rowsLength={2}
                />
                <Grid item md={4} sm={12} xs={12} alignItems={'center'}>
                  <CheckboxController label="Location Open" name="isLocationOpen" />
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || updating}
              aria-label="add new client"
              endIcon={(loading || updating) && <CircularProgress size={20} color="inherit" />}
            >
              {office?.id ? 'Update' : 'Add'} Profit Center
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default OfficeModal
